import React from 'react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { calculateScaffoldLoad } from '../Editor/ScaffoldingSystem';
import { exportScaffoldReport } from '../Editor/ScaffoldReportExport';

/**
 * Scaffolding configuration panel — extracted from PropertiesPanel.js.
 * Manages scaffold units (primary + supports), decks, stairs, railings, counterweights, and load calculations.
 */
const ScaffoldingProperties = ({ 
  selectedObject, 
  updateSceneObjectWithMulti, 
  activeScaffoldTab, 
  setActiveScaffoldTab, 
  unit,
  editDeckLevel,
  setEditDeckLevel,
  editDeckIndex,
  setEditDeckIndex,
  editStairLevel,
  setEditStairLevel
}) => {
  const sc = selectedObject.scaffoldConfig;
  const supports = sc.supportScaffolds || [];

  // Active scaffold unit: 'primary' or support index
  const activeSupIdx = activeScaffoldTab === 'primary' ? null : (typeof activeScaffoldTab === 'number' ? activeScaffoldTab : null);
  const isSupport = activeSupIdx !== null && activeSupIdx < supports.length;
  const activeCfg = isSupport ? supports[activeSupIdx] : sc;

  const decks = activeCfg.showDecks || [];
  const stairs = activeCfg.showStairs || [];
  const H = activeCfg.height || 3;
  const W = activeCfg.width || 3;
  const D = activeCfg.depth || 2;

  // Unified update: patches either primary or the active support
  const updateActiveCfg = (patch) => {
    if (isSupport) {
      const newSupports = [...supports];
      newSupports[activeSupIdx] = { ...newSupports[activeSupIdx], ...patch };
      updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, supportScaffolds: newSupports } });
    } else {
      updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, ...patch } });
    }
  };

  // Add a new support scaffold
  const addSupportScaffold = () => {
    const newSup = {
      side: 'back',
      distance: 0,
      alignEdge: 'center',
      width: Math.max(1, sc.width || 3),
      depth: 2,
      height: Math.max(1, Math.round((sc.height || 3) / 2)),
      showDecks: [],
      showStairs: [],
      deckLevels: {},
      stairLevels: {},
      topRailing: {}
    };
    const newSupports = [...supports, newSup];
    updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, supportScaffolds: newSupports } });
    setActiveScaffoldTab(newSupports.length - 1);
    setEditDeckLevel(null);
    setEditStairLevel(null);
  };

  // Remove a support scaffold
  const removeSupportScaffold = (idx) => {
    const newSupports = supports.filter((_, i) => i !== idx);
    updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, supportScaffolds: newSupports } });
    setActiveScaffoldTab('primary');
    setEditDeckLevel(null);
    setEditStairLevel(null);
  };

  // Helper to get all deck configs for a level (array, backward compat)
  const getDeckLCs = (lvl) => {
    const dl = activeCfg.deckLevels?.[String(lvl)];
    const def = { width: null, depth: null, alignH: 'center', alignD: 'center', shiftX: 0, shiftZ: 0 };
    if (!dl) return [{ ...def }];
    if (Array.isArray(dl)) return dl.length > 0 ? dl : [{ ...def }];
    return [dl];
  };

  // Helper to get per-level stair config
  const getStairLC = (lvl) => {
    const sl = activeCfg.stairLevels?.[String(lvl)];
    return { rotation: sl?.rotation || 'front', shiftX: sl?.shiftX ?? 0, shiftZ: sl?.shiftZ ?? 0 };
  };

  // Update a specific deck at index within a level
  const updateDeckAtIndex = (lvl, idx, patch) => {
    const arr = [...getDeckLCs(lvl)];
    arr[idx] = { ...arr[idx], ...patch };
    updateActiveCfg({ deckLevels: { ...(activeCfg.deckLevels || {}), [String(lvl)]: arr } });
  };

  // Add new deck to a level
  const addDeckToLevel = (lvl) => {
    const arr = [...getDeckLCs(lvl), { width: null, depth: null, alignH: 'center', alignD: 'center', shiftX: 0, shiftZ: 0 }];
    updateActiveCfg({ deckLevels: { ...(activeCfg.deckLevels || {}), [String(lvl)]: arr } });
    setEditDeckIndex(arr.length - 1);
  };

  // Remove a deck at index
  const removeDeckAtIndex = (lvl, idx) => {
    const arr = [...getDeckLCs(lvl)];
    if (arr.length <= 1) return;
    arr.splice(idx, 1);
    updateActiveCfg({ deckLevels: { ...(activeCfg.deckLevels || {}), [String(lvl)]: arr } });
    setEditDeckIndex(Math.max(0, idx - 1));
  };

  // Helper to update a single stair level config
  const updateStairLevel = (lvl, patch) => {
    const existing = getStairLC(lvl);
    const updated = { ...existing, ...patch };
    updateActiveCfg({ stairLevels: { ...(activeCfg.stairLevels || {}), [String(lvl)]: updated } });
  };

  // Current editing level configs
  const deckLCsForLevel = editDeckLevel ? getDeckLCs(editDeckLevel) : [];
  const safeDeckIdx = Math.min(editDeckIndex, Math.max(0, deckLCsForLevel.length - 1));
  const deckLC = deckLCsForLevel[safeDeckIdx] || null;
  const stairLC = editStairLevel ? getStairLC(editStairLevel) : null;

  return (
  <div className="space-y-3 border border-zinc-700 rounded-lg p-3">
    <div className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Scaffolding Config</div>

    {/* === SCAFFOLD UNIT TABS === */}
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1 items-center">
        <button
          onClick={() => { setActiveScaffoldTab('primary'); setEditDeckLevel(null); setEditStairLevel(null); }}
          className={`px-2 py-0.5 text-[10px] rounded border transition-all ${
            !isSupport
              ? 'bg-violet-500/40 border-violet-400 text-violet-200 ring-1 ring-violet-400'
              : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
          }`}
          data-testid="scaffold-tab-primary"
        >Primary</button>
        {supports.map((sup, si) => (
          <button key={sup?.id || `support-${si}`}
            onClick={() => { setActiveScaffoldTab(si); setEditDeckLevel(null); setEditStairLevel(null); }}
            className={`px-2 py-0.5 text-[10px] rounded border transition-all ${
              activeSupIdx === si
                ? 'bg-cyan-500/40 border-cyan-400 text-cyan-200 ring-1 ring-cyan-400'
                : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
            }`}
            data-testid={`scaffold-tab-support-${si}`}
          >
            Sup {si + 1}
            <span onClick={(e) => { e.stopPropagation(); removeSupportScaffold(si); }}
              className="ml-1 text-red-400 hover:text-red-300 cursor-pointer">x</span>
          </button>
        ))}
        <button onClick={addSupportScaffold}
          className="px-1.5 py-0.5 text-[9px] bg-cyan-700/30 hover:bg-cyan-600/30 rounded text-cyan-300 border border-cyan-600"
          data-testid="scaffold-add-support"
        >+ Support</button>
      </div>
    </div>

    {/* === SUPPORT POSITION CONTROLS (only for support tabs) === */}
    {isSupport && (
      <div className="space-y-2 border border-cyan-700/30 rounded p-2 bg-cyan-900/10">
        <Label className="text-[10px] text-cyan-300 font-semibold">Position (relative to Primary)</Label>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Side</Label>
            <select value={activeCfg.side || 'back'}
              onChange={(e) => updateActiveCfg({ side: e.target.value })}
              className="w-full h-6 text-[10px] bg-zinc-800 border border-zinc-700 rounded text-zinc-300 px-1"
              data-testid="scaffold-support-side"
            >
              <option value="back">Back</option>
              <option value="front">Front</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Distance (m)</Label>
            <Input type="number" min={0} max={10} step={1}
              value={activeCfg.distance ?? 0}
              onChange={(e) => updateActiveCfg({ distance: Math.max(0, parseInt(e.target.value) || 0) })}
              className="h-6 text-[10px]" data-testid="scaffold-support-distance"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Align</Label>
            <select value={activeCfg.alignEdge || 'center'}
              onChange={(e) => updateActiveCfg({ alignEdge: e.target.value })}
              className="w-full h-6 text-[10px] bg-zinc-800 border border-zinc-700 rounded text-zinc-300 px-1"
              data-testid="scaffold-support-align"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
      </div>
    )}
    
    {/* W x D x H — cap at 200m which is well beyond any realistic AV truss
        rig and keeps the thin-instance count from blowing up. */}
    <div className="grid grid-cols-3 gap-2">
      <div className="space-y-1">
        <Label className="text-[10px] text-zinc-400">Width (m)</Label>
        <Input type="number" min={1} max={200} step={1}
          value={W}
          onChange={(e) => {
            const val = Math.max(1, Math.min(200, parseInt(e.target.value) || 1));
            updateActiveCfg({ width: val });
          }}
          className="h-7 text-xs" data-testid="scaffold-width"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] text-zinc-400">Depth (m)</Label>
        <Input type="number" min={1} max={200} step={1}
          value={D}
          onChange={(e) => {
            const val = Math.max(1, Math.min(200, parseInt(e.target.value) || 1));
            updateActiveCfg({ depth: val });
          }}
          className="h-7 text-xs" data-testid="scaffold-depth"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] text-zinc-400">Height (m)</Label>
        <Input type="number" min={1} max={200} step={1}
          value={H}
          onChange={(e) => {
            const val = Math.max(1, Math.min(200, parseInt(e.target.value) || 1));
            if (!isSupport) {
              // Keep any user-placed decks that still fit under the new
              // height. If none survive (or none existed), drop a single
              // default deck on the new TOP level — matches the create-time
              // default and gives the operator a clean, top-only starting
              // point instead of decks at every level.
              const prev = activeCfg.showDecks || [];
              const surviving = prev.filter(lvl => lvl >= 1 && lvl <= val);
              const next = surviving.length > 0 ? surviving : [val];
              updateActiveCfg({ height: val, showDecks: next });
            } else {
              updateActiveCfg({ height: val });
            }
          }}
          className="h-7 text-xs" data-testid="scaffold-height"
        />
      </div>
    </div>

    {/* === DECK SECTION === */}
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] text-zinc-400">Decks <span className="text-zinc-600">(click to edit, dbl-click to remove)</span></Label>
        <div className="flex gap-1">
          <button onClick={() => {
            const all = [];
            for (let i = 1; i <= H; i++) all.push(i);
            updateActiveCfg({ showDecks: all });
          }} className="px-1.5 py-0 text-[9px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600" data-testid="scaffold-deck-all">All</button>
          <button onClick={() => {
            updateActiveCfg({ showDecks: [] });
            setEditDeckLevel(null);
          }} className="px-1.5 py-0 text-[9px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600" data-testid="scaffold-deck-none">None</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: H }, (_, i) => i + 1).map(level => {
          const hasDeck = decks.includes(level);
          const isEditing = editDeckLevel === level && hasDeck;
          return (
            <button
              key={level}
              onClick={() => {
                if (hasDeck) {
                  setEditDeckLevel(isEditing ? null : level);
                } else {
                  const newDecks = [...decks, level].sort((a, b) => a - b);
                  updateActiveCfg({ showDecks: newDecks });
                  setEditDeckLevel(level);
                }
              }}
              onDoubleClick={() => {
                if (hasDeck) {
                  const newDecks = decks.filter(d => d !== level);
                  updateActiveCfg({ showDecks: newDecks });
                  if (editDeckLevel === level) setEditDeckLevel(null);
                }
              }}
              className={`px-2 py-0.5 text-[10px] rounded border transition-all ${
                isEditing
                  ? 'bg-amber-500/40 border-amber-400 text-amber-200 ring-1 ring-amber-400'
                  : hasDeck
                    ? 'bg-amber-600/30 border-amber-500 text-amber-300'
                    : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid={`scaffold-deck-${level}`}
            >
              L{level}
            </button>
          );
        })}
      </div>
    </div>

    {/* Per-level deck controls (multi-deck) */}
    {editDeckLevel && decks.includes(editDeckLevel) && (
      <div className="space-y-2 border border-amber-700/40 rounded p-2 bg-amber-900/10">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-amber-300 font-semibold">Deck L{editDeckLevel}</Label>
          <div className="flex gap-1">
            <button onClick={() => addDeckToLevel(editDeckLevel)}
              className="px-1.5 py-0 text-[9px] bg-amber-700/40 hover:bg-amber-600/40 rounded text-amber-300 border border-amber-600"
              data-testid="scaffold-add-deck">+ Add Deck</button>
            <button onClick={() => {
              const newDecks = decks.filter(d => d !== editDeckLevel);
              updateActiveCfg({ showDecks: newDecks });
              setEditDeckLevel(null);
            }} className="text-[9px] text-red-400 hover:text-red-300 px-1">Remove Level</button>
          </div>
        </div>
        {/* Deck selector tabs */}
        <div className="flex flex-wrap gap-1">
          {deckLCsForLevel.map((_, di) => (
            <button key={`${editDeckLevel}-deck-${di}`} onClick={() => setEditDeckIndex(di)}
              className={`px-2 py-0.5 text-[10px] rounded border ${
                safeDeckIdx === di
                  ? 'bg-amber-500/40 border-amber-400 text-amber-200 ring-1 ring-amber-400'
                  : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid={`scaffold-deck-tab-${di}`}
            >
              Deck {di + 1}
              {deckLCsForLevel.length > 1 && (
                <span onClick={(e) => { e.stopPropagation(); removeDeckAtIndex(editDeckLevel, di); }}
                  className="ml-1 text-red-400 hover:text-red-300 cursor-pointer">x</span>
              )}
            </button>
          ))}
        </div>
        {/* Selected deck controls */}
        {deckLC && (
        <>
        {/* Size */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Width (modules)</Label>
            <Input type="number" min={1} max={W} step={1}
              value={deckLC.width ?? W}
              onChange={(e) => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { width: Math.max(1, Math.min(W, parseInt(e.target.value) || 1)) })}
              className="h-6 text-[10px]" data-testid="scaffold-deck-width"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Depth (modules)</Label>
            <Input type="number" min={1} max={D} step={1}
              value={deckLC.depth ?? D}
              onChange={(e) => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { depth: Math.max(1, Math.min(D, parseInt(e.target.value) || 1)) })}
              className="h-6 text-[10px]" data-testid="scaffold-deck-depth"
            />
          </div>
        </div>
        {/* Alignment */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Horizontal</Label>
            <select value={deckLC.alignH}
              onChange={(e) => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { alignH: e.target.value })}
              className="w-full h-6 text-[10px] bg-zinc-800 border border-zinc-700 rounded text-zinc-300 px-1"
              data-testid="scaffold-deck-align-h"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-500">Depth</Label>
            <select value={deckLC.alignD}
              onChange={(e) => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { alignD: e.target.value })}
              className="w-full h-6 text-[10px] bg-zinc-800 border border-zinc-700 rounded text-zinc-300 px-1"
              data-testid="scaffold-deck-align-d"
            >
              <option value="front">Front</option>
              <option value="center">Center</option>
              <option value="back">Back</option>
            </select>
          </div>
        </div>
        {/* Shift X (mm) */}
        <div className="space-y-1">
          <Label className="text-[10px] text-zinc-500">Shift X (mm)</Label>
          <div className="flex items-center gap-1">
            <button onClick={() => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { shiftX: (deckLC.shiftX || 0) - 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-deck-shift-x-minus">-10</button>
            <Input type="number" step={1}
              value={deckLC.shiftX || 0}
              onChange={(e) => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { shiftX: parseInt(e.target.value) || 0 })}
              className="h-6 text-[10px] flex-1 text-center" data-testid="scaffold-deck-shift-x"
            />
            <button onClick={() => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { shiftX: (deckLC.shiftX || 0) + 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-deck-shift-x-plus">+10</button>
          </div>
        </div>
        {/* Shift Z (mm) */}
        <div className="space-y-1">
          <Label className="text-[10px] text-zinc-500">Shift Z (mm)</Label>
          <div className="flex items-center gap-1">
            <button onClick={() => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { shiftZ: (deckLC.shiftZ || 0) - 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-deck-shift-z-minus">-10</button>
            <Input type="number" step={1}
              value={deckLC.shiftZ || 0}
              onChange={(e) => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { shiftZ: parseInt(e.target.value) || 0 })}
              className="h-6 text-[10px] flex-1 text-center" data-testid="scaffold-deck-shift-z"
            />
            <button onClick={() => updateDeckAtIndex(editDeckLevel, safeDeckIdx, { shiftZ: (deckLC.shiftZ || 0) + 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-deck-shift-z-plus">+10</button>
          </div>
        </div>
        </>
        )}
      </div>
    )}

    {/* === STAIR SECTION === */}
    <div className="space-y-2">
      <Label className="text-[10px] text-zinc-400">Stairs <span className="text-zinc-600">(click to toggle, click enabled to edit)</span></Label>
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: H }, (_, i) => i + 1).map(level => {
          const hasStair = stairs.includes(level);
          const isEditing = editStairLevel === level && hasStair;
          return (
            <button
              key={level}
              onClick={() => {
                if (hasStair) {
                  setEditStairLevel(isEditing ? null : level);
                } else {
                  const newStairs = [...stairs, level].sort((a, b) => a - b);
                  updateActiveCfg({ showStairs: newStairs });
                  setEditStairLevel(level);
                }
              }}
              onDoubleClick={() => {
                if (hasStair) {
                  const newStairs = stairs.filter(s => s !== level);
                  updateActiveCfg({ showStairs: newStairs });
                  if (editStairLevel === level) setEditStairLevel(null);
                }
              }}
              className={`px-2 py-0.5 text-[10px] rounded border transition-all ${
                isEditing
                  ? 'bg-blue-500/40 border-blue-400 text-blue-200 ring-1 ring-blue-400'
                  : hasStair
                    ? 'bg-blue-600/30 border-blue-500 text-blue-300'
                    : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid={`scaffold-stair-${level}`}
            >
              L{level}
            </button>
          );
        })}
      </div>
    </div>

    {/* Per-level stair controls */}
    {editStairLevel && stairs.includes(editStairLevel) && stairLC && (
      <div className="space-y-2 border border-blue-700/40 rounded p-2 bg-blue-900/10">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-blue-300 font-semibold">Stair L{editStairLevel} Settings</Label>
          <button onClick={() => {
            const newStairs = stairs.filter(s => s !== editStairLevel);
            updateActiveCfg({ showStairs: newStairs });
            setEditStairLevel(null);
          }} className="text-[9px] text-red-400 hover:text-red-300 px-1">Remove</button>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-zinc-500">Direction</Label>
          <select value={stairLC.rotation}
            onChange={(e) => updateStairLevel(editStairLevel, { rotation: e.target.value })}
            className="w-full h-7 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 px-2"
            data-testid="scaffold-stair-rotation"
          >
            <option value="front">Front</option>
            <option value="back">Back</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
        {/* Stair Shift X (mm) */}
        <div className="space-y-1">
          <Label className="text-[10px] text-zinc-500">Shift X (mm)</Label>
          <div className="flex items-center gap-1">
            <button onClick={() => updateStairLevel(editStairLevel, { shiftX: (stairLC.shiftX || 0) - 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-stair-shift-x-minus">-10</button>
            <Input type="number" step={1}
              value={stairLC.shiftX || 0}
              onChange={(e) => updateStairLevel(editStairLevel, { shiftX: parseInt(e.target.value) || 0 })}
              className="h-6 text-[10px] flex-1 text-center" data-testid="scaffold-stair-shift-x"
            />
            <button onClick={() => updateStairLevel(editStairLevel, { shiftX: (stairLC.shiftX || 0) + 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-stair-shift-x-plus">+10</button>
          </div>
        </div>
        {/* Stair Shift Z (mm) */}
        <div className="space-y-1">
          <Label className="text-[10px] text-zinc-500">Shift Z (mm)</Label>
          <div className="flex items-center gap-1">
            <button onClick={() => updateStairLevel(editStairLevel, { shiftZ: (stairLC.shiftZ || 0) - 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-stair-shift-z-minus">-10</button>
            <Input type="number" step={1}
              value={stairLC.shiftZ || 0}
              onChange={(e) => updateStairLevel(editStairLevel, { shiftZ: parseInt(e.target.value) || 0 })}
              className="h-6 text-[10px] flex-1 text-center" data-testid="scaffold-stair-shift-z"
            />
            <button onClick={() => updateStairLevel(editStairLevel, { shiftZ: (stairLC.shiftZ || 0) + 10 })}
              className="px-1.5 py-0.5 text-[10px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
              data-testid="scaffold-stair-shift-z-plus">+10</button>
          </div>
        </div>
      </div>
    )}

    {/* === TOP RAILING === */}
    <div className="space-y-2">
      <Label className="text-[10px] text-zinc-400">Top Railing</Label>
      <div className="flex flex-wrap gap-1">
        {['front', 'back', 'left', 'right'].map(side => {
          const tr = activeCfg.topRailing || {};
          const isOn = !!tr[side];
          return (
            <button
              key={side}
              onClick={() => {
                const newTR = { ...tr, [side]: !isOn };
                updateActiveCfg({ topRailing: newTR });
              }}
              className={`px-2 py-0.5 text-[10px] rounded border capitalize ${
                isOn
                  ? 'bg-green-600/30 border-green-500 text-green-300'
                  : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid={`scaffold-railing-${side}`}
            >
              {side}
            </button>
          );
        })}
      </div>
    </div>

    {/* === COUNTERWEIGHT SYSTEM === */}
    {(() => {
      const cwList = activeCfg.counterweights || [];
      const cwCount = cwList.length;
      const totalWeight = cwCount * 1000;

      const toggleCW = (gx, gz) => {
        const exists = cwList.findIndex(c => c.gridX === gx && c.gridZ === gz);
        let updated;
        if (exists >= 0) {
          updated = cwList.filter((_, i) => i !== exists);
        } else {
          updated = [...cwList, { gridX: gx, gridZ: gz }];
        }
        updateActiveCfg({ counterweights: updated });
      };

      const distributeEvenly = () => {
        const all = [];
        for (let gx = 0; gx < W; gx++) {
          for (let gz = 0; gz < D; gz++) {
            all.push({ gridX: gx, gridZ: gz });
          }
        }
        updateActiveCfg({ counterweights: all });
      };

      const removeAllCW = () => {
        updateActiveCfg({ counterweights: [] });
      };

      return (
      <div className="space-y-2 border border-zinc-600/50 rounded p-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-zinc-300 font-semibold">Counterweights</Label>
          <span className="text-[9px] text-zinc-500" data-testid="cw-total-weight">
            {cwCount > 0 ? `${totalWeight.toLocaleString()} kg (${cwCount} unit${cwCount > 1 ? 's' : ''})` : 'None'}
          </span>
        </div>

        {/* Bay grid — click to place/remove */}
        <div className="space-y-1">
          <Label className="text-[9px] text-zinc-500">Click bay to place/remove (900x900x900mm, 1000 kg each)</Label>
          <div className="inline-grid gap-[2px] p-1 bg-zinc-900/50 rounded" style={{ gridTemplateColumns: `repeat(${W}, 1fr)` }}>
            {Array.from({ length: D }, (_, dIdx) => {
              const gz = D - 1 - dIdx; // top row = back (higher Z)
              return Array.from({ length: W }, (__, gx) => {
                const hasCW = cwList.some(c => c.gridX === gx && c.gridZ === gz);
                return (
                  <button
                    key={`cw-${gx}-${gz}`}
                    onClick={() => toggleCW(gx, gz)}
                    title={`Bay (${gx}, ${gz})${hasCW ? ' - 1000 KG' : ''}`}
                    className={`w-6 h-6 rounded-sm text-[7px] font-bold border transition-all ${
                      hasCW
                        ? 'bg-slate-400/60 border-slate-300 text-slate-100 ring-1 ring-slate-300'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-600 hover:border-zinc-500 hover:bg-zinc-700'
                    }`}
                    data-testid={`cw-bay-${gx}-${gz}`}
                  >
                    {hasCW ? 'CW' : ''}
                  </button>
                );
              });
            }).flat()}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-1">
          <button onClick={distributeEvenly}
            className="px-2 py-0.5 text-[9px] bg-zinc-700 hover:bg-zinc-600 rounded text-zinc-300 border border-zinc-600"
            data-testid="cw-distribute">Distribute</button>
          <button onClick={removeAllCW}
            className="px-2 py-0.5 text-[9px] bg-red-900/30 hover:bg-red-800/30 rounded text-red-300 border border-red-700/50"
            data-testid="cw-remove-all">Remove All</button>
        </div>

        {/* Settings row */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[9px] text-zinc-500">Color</Label>
            <input type="color"
              value={sc.counterweightColor || '#A0A0A0'}
              onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, counterweightColor: e.target.value } })}
              className="w-full h-6 rounded cursor-pointer bg-transparent border border-zinc-700"
              data-testid="cw-color"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[9px] text-zinc-500">Show Labels</Label>
            <button
              onClick={() => updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, showCWLabels: !(sc.showCWLabels !== false) } })}
              className={`w-full h-6 text-[9px] rounded border ${
                sc.showCWLabels !== false
                  ? 'bg-green-600/30 border-green-500 text-green-300'
                  : 'bg-zinc-800 border-zinc-600 text-zinc-400'
              }`}
              data-testid="cw-labels-toggle"
            >
              {sc.showCWLabels !== false ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
      </div>
      );
    })()}

    {/* === LOAD CALCULATION & MATERIAL SELECTOR === */}
    {(() => {
      const loadData = calculateScaffoldLoad(sc);
      const li = sc.loadInputs || {};
      const topInput = li.topLoad || 0;
      const sideInput = li.sideLoad || 0;
      const sideLoadSides = li.sideLoadSides || {};
      const activeSides = Object.keys(sideLoadSides).filter(s => sideLoadSides[s]);

      const topAvail = loadData.topLoad.available;
      const topRatio = topAvail > 0 ? topInput / topAvail : 0;
      const topStatus = topInput <= 0 ? null : topInput <= topAvail ? 'SAFE' : 'OVERLOAD';

      const updateLoadInputs = (patch) => {
        updateSceneObjectWithMulti(selectedObject.id, {
          scaffoldConfig: { ...sc, loadInputs: { ...li, ...patch } }
        });
      };

      return (
      <div className="space-y-3 border border-zinc-600/50 rounded p-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-zinc-300 font-semibold">Load Calculation</Label>
          <span className="text-[8px] text-zinc-500">OSHA {loadData.oshaFactor}:1</span>
        </div>

        {/* Material selector */}
        <div className="space-y-1">
          <Label className="text-[9px] text-zinc-500">Material Type</Label>
          <div className="flex gap-1">
            {['aluminium', 'steel'].map(mt => (
              <button key={mt}
                onClick={() => updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, materialType: mt } })}
                className={`flex-1 px-2 py-1 text-[10px] rounded border capitalize transition-all ${
                  (sc.materialType || 'aluminium') === mt
                    ? 'bg-violet-500/40 border-violet-400 text-violet-200 ring-1 ring-violet-400'
                    : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
                }`}
                data-testid={`scaffold-material-${mt}`}
              >{mt}</button>
            ))}
          </div>
        </div>

        {/* Auto-calculated summary */}
        <div className="space-y-1 bg-zinc-900/50 rounded p-2">
          <div className="flex justify-between text-[9px]">
            <span className="text-zinc-500">Scaffold Weight:</span>
            <span className="text-zinc-300 font-mono" data-testid="load-scaffold-weight">{loadData.weights.total.toFixed(0)} kg</span>
          </div>
          <div className="flex justify-between text-[9px]">
            <span className="text-zinc-500">Total Pipes:</span>
            <span className="text-zinc-300 font-mono">{loadData.totalPipes}</span>
          </div>
          <div className="flex justify-between text-[9px]">
            <span className="text-zinc-500">Top Load Capacity:</span>
            <span className="text-green-300 font-mono font-bold" data-testid="load-top-capacity">{topAvail.toLocaleString()} kg</span>
          </div>
          {activeSides.map(side => (
            <div key={side} className="flex justify-between text-[9px]">
              <span className="text-zinc-500 capitalize">{side} Side Capacity:</span>
              <span className="text-green-300 font-mono">{loadData.sideCapacities[side].toLocaleString()} kg</span>
            </div>
          ))}
        </div>

        {/* User Load Inputs */}
        <div className="space-y-2">
          <Label className="text-[9px] text-zinc-400 font-semibold">Enter Your Loads</Label>
          {/* Top Load */}
          <div className="space-y-1">
            <Label className="text-[9px] text-zinc-500">Top Load (kg)</Label>
            <Input type="number" min={0} step={100}
              value={topInput}
              onChange={(e) => updateLoadInputs({ topLoad: Math.max(0, parseInt(e.target.value) || 0) })}
              className="h-6 text-[10px]" data-testid="load-top-input"
            />
            {topStatus && (
              <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold ${
                topStatus === 'SAFE' ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'
              }`} data-testid="load-top-status">
                {topStatus === 'SAFE' ? '✅' : '⚠️'} Top: {topInput.toLocaleString()} kg / {topAvail.toLocaleString()} kg — {topStatus}
                {topRatio > 0 && <span className="ml-auto text-zinc-400">({(topRatio * 100).toFixed(0)}%)</span>}
              </div>
            )}
          </div>

          {/* Side Load */}
          <div className="space-y-1">
            <Label className="text-[9px] text-zinc-500">Side Hanging Load (kg)</Label>
            <Input type="number" min={0} step={100}
              value={sideInput}
              onChange={(e) => updateLoadInputs({ sideLoad: Math.max(0, parseInt(e.target.value) || 0) })}
              className="h-6 text-[10px]" data-testid="load-side-input"
            />
            <Label className="text-[9px] text-zinc-500">Apply to sides:</Label>
            <div className="flex gap-1">
              {['front', 'back', 'left', 'right'].map(side => (
                <button key={side}
                  onClick={() => updateLoadInputs({
                    sideLoadSides: { ...sideLoadSides, [side]: !sideLoadSides[side] }
                  })}
                  className={`flex-1 px-1 py-0.5 text-[9px] rounded border capitalize ${
                    sideLoadSides[side]
                      ? 'bg-cyan-600/30 border-cyan-500 text-cyan-300'
                      : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
                  }`}
                  data-testid={`load-side-${side}`}
                >{side}</button>
              ))}
            </div>
            {sideInput > 0 && activeSides.length > 0 && (
              <div className="space-y-0.5">
                {activeSides.map(side => {
                  const cap = loadData.sideCapacities[side];
                  const ratio = cap > 0 ? sideInput / cap : 0;
                  const safe = sideInput <= cap;
                  return (
                    <div key={side} className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold ${
                      safe ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'
                    }`} data-testid={`load-side-status-${side}`}>
                      {safe ? '✅' : '⚠️'} {side.charAt(0).toUpperCase() + side.slice(1)}: {sideInput.toLocaleString()} / {cap.toLocaleString()} kg — {safe ? 'SAFE' : 'OVERLOAD'}
                      <span className="ml-auto text-zinc-400">({(ratio * 100).toFixed(0)}%)</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Export PDF button */}
        <button
          onClick={async () => {
            try {
              toast.info('Generating scaffold report...');
              const engine = window.__babylonEngine;
              const scene = window.__babylonScene;
              if (!engine || !scene) { toast.error('3D engine not ready'); return; }
              const fileName = await exportScaffoldReport(scene, engine, sc, 'Scaffold Project', 'User');
              toast.success(`Report exported: ${fileName}`);
            } catch (err) {
              toast.error('Export failed: ' + (err.message || 'Unknown error'));
            }
          }}
          className="w-full px-3 py-1.5 text-[10px] bg-indigo-600/40 hover:bg-indigo-500/40 rounded border border-indigo-500 text-indigo-200 font-semibold transition-all"
          data-testid="scaffold-export-pdf"
        >
          Export Scaffold Report (PDF)
        </button>

        {/* Show/Hide load highlights toggle */}
        <div className="flex items-center justify-between">
          <Label className="text-[9px] text-zinc-500">Load Highlights</Label>
          <button
            onClick={() => updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, showLoadHighlights: !(sc.showLoadHighlights !== false) } })}
            className={`px-3 py-0.5 text-[9px] rounded border ${
              sc.showLoadHighlights !== false
                ? 'bg-green-600/30 border-green-500 text-green-300'
                : 'bg-zinc-800 border-zinc-600 text-zinc-400'
            }`}
            data-testid="scaffold-load-highlights-toggle"
          >
            {sc.showLoadHighlights !== false ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>
      );
    })()}

    {/* Color controls */}
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1">
        <Label className="text-[10px] text-zinc-400">Structure</Label>
        <input type="color"
          value={sc.structureColor || '#C0C0C0'}
          onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, structureColor: e.target.value } })}
          className="w-full h-7 rounded cursor-pointer bg-transparent border border-zinc-700"
          data-testid="scaffold-structure-color"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] text-zinc-400">Deck</Label>
        <input type="color"
          value={sc.deckColor || '#D9D9D9'}
          onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { scaffoldConfig: { ...sc, deckColor: e.target.value } })}
          className="w-full h-7 rounded cursor-pointer bg-transparent border border-zinc-700"
          data-testid="scaffold-deck-color"
        />
      </div>
    </div>
    
    <p className="text-[10px] text-zinc-500">
      Base module: 1m x 1m x 1m | Shift in mm | Dbl-click level to remove
    </p>
  </div>
  );
};

export default ScaffoldingProperties;
